(() => {
  let authoritativeFilteredCount = null;
  const originalStats = new WeakMap();
  const rememberStats = () => document.querySelectorAll(
    '.collection-hero__stats .stat, .make-hero__stats .stat, .stat-strip .stat',
  ).forEach((stat) => {
    if (!originalStats.has(stat)) originalStats.set(stat, {
      value: stat.querySelector('strong')?.textContent || '',
      label: stat.querySelector('span')?.textContent || '',
    });
  });

  // Keep an explicit brand refinement when following range navigation. The
  // original URL is retained so clearing the filter also clears this addition.
  const scopedBrandLinks = new WeakMap();
  const syncBrandNavigation = () => {
    const current = new URLSearchParams(window.location.search);
    const vendor = current.get('filter.p.vendor') || current.get('catalogue_brand');
    document.querySelectorAll('main .range-bar a[href], main .breadcrumbs a[href], main [aria-label="Collection breadcrumb"] a[href], main .collection-category__grid a[href], main #fitment a[href*="/pages/vehicle-search"]').forEach((link) => {
      const href = link.getAttribute('href');
      let saved = scopedBrandLinks.get(link);
      if (!saved || (href !== saved.applied && href !== saved.original)) saved = { original: href, applied: href };
      const url = new URL(saved.original, window.location.origin);
      if (url.origin !== window.location.origin || (!url.pathname.startsWith('/collections/') && url.pathname !== '/pages/vehicle-search') || url.pathname.includes('/products/')) return;
      if (vendor && !url.searchParams.has('filter.p.vendor') && !url.searchParams.has('catalogue_brand')) url.searchParams.set('filter.p.vendor', vendor);
      const next = url.pathname + url.search + url.hash;
      if (href !== next) link.setAttribute('href', next);
      saved.applied = next;
      scopedBrandLinks.set(link, saved);
    });
  };
  document.addEventListener('click', syncBrandNavigation, true);
  window.addEventListener('popstate', syncBrandNavigation);


  const filteredResultContext = () => {
    const params = new URLSearchParams(window.location.search);
    const nativeFilters = [...params.keys()].filter((key) => key.startsWith('filter.'));
    const vendorValues = params
      .getAll('filter.p.vendor')
      .flatMap((value) => value.split(','))
      .map((value) => value.trim())
      .filter(Boolean);

    return {
      active: nativeFilters.length > 0 || params.has('catalogue_brand') || params.has('brand_category'),
      nativeActive: nativeFilters.length > 0,
      vendorCount: new Set(vendorValues.map((value) => value.toLowerCase())).size,
    };
  };

  const replaceCountText = (node, count, suffix = 'in this filtered range') => {
    if (!node) return;
    const noun = count === 1 ? 'product' : 'products';
    if (node.textContent.trim() === `${count} ${noun} ${suffix}`) return;
    const pulse = node.querySelector('.pulse');
    node.replaceChildren();
    if (pulse) node.appendChild(pulse);
    node.appendChild(document.createTextNode(`${count} ${noun} ${suffix}`));
  };

  const syncFilteredCollectionCounts = (root = document, exactCount = null) => {
    syncBrandNavigation();
    rememberStats();
    const context = filteredResultContext();

    const controls = root.querySelector('[data-all-products-controls]')
      || document.querySelector('[data-all-products-controls]');
    const countStatusNodes = [...root.querySelectorAll(
      '[data-all-products-count-status], #ProductCountDesktop, #ProductCount',
    )];
    const statusText = countStatusNodes.map((node) => node.textContent?.trim() || '').find(Boolean) || '';
    let count = Number.isFinite(exactCount)
      ? Math.trunc(exactCount)
      : Number.parseInt(controls?.dataset.totalProducts || '', 10);
    const terminalOfMatch = statusText.match(/^Showing\s+\d+\s+of\s+(\d+)\s+matching\s+products?$/i);
    const terminalMatch = statusText.match(/^Showing\s+(\d+)\s+matching\s+products?$/i);
    if (!Number.isFinite(exactCount) && terminalOfMatch) count = Number.parseInt(terminalOfMatch[1], 10);
    if (!Number.isFinite(exactCount) && terminalMatch) count = Number.parseInt(terminalMatch[1], 10);
    if (!Number.isFinite(count) || count < 0) return;
    if (!context.active) {
      authoritativeFilteredCount = null;
      root.querySelectorAll('.collection-hero__count').forEach((node) => replaceCountText(node, count, 'in this range'));
      root.querySelectorAll('.range-bar__info span').forEach((node) => {
        if (/\d+\s+products?\s+in\s+this\s+(?:filtered\s+)?range/i.test(node.textContent || '')) {
          replaceCountText(node, count, 'in this range');
        }
      });
      root.querySelectorAll('.collection-hero__stats .stat, .make-hero__stats .stat, .stat-strip .stat').forEach((stat) => {
        const saved = originalStats.get(stat), strong = stat.querySelector('strong'), label = stat.querySelector('span');
        if (!saved || !strong || !label) return;
        if (stat.dataset.filteredCollectionBrandCount || stat.dataset.filteredCollectionCategoryCount) {
          if (strong.textContent !== saved.value) strong.textContent = saved.value;
          if (label.textContent !== saved.label) label.textContent = saved.label;
        }
        if (stat.dataset.filteredCollectionCount) {
          const value = `${count} ${count === 1 ? 'product' : 'products'}`;
          if (strong.textContent !== value) strong.textContent = value;
          if (label.textContent !== saved.label) label.textContent = saved.label;
        }
        delete stat.dataset.filteredCollectionBrandCount;
        delete stat.dataset.filteredCollectionCategoryCount;
        delete stat.dataset.filteredCollectionCount;
      });
      return;
    }
    const filteredBrandCount = Number.parseInt(controls?.dataset.filteredBrandCount || '', 10);
    const filteredCategoryCount = Number.parseInt(controls?.dataset.filteredCategoryCount || '', 10);

    if (context.nativeActive && /matches loaded so far$/i.test(statusText)) {
      const shownMatch = statusText.match(/^Showing\s+(\d+)/i);
      const shown = Number.parseInt(shownMatch?.[1] || '', 10);
      if (Number.isFinite(shown)) {
        const corrected = shown === count
          ? `Showing ${count} matching ${count === 1 ? 'product' : 'products'}`
          : `Showing ${shown} of ${count} matching products`;
        countStatusNodes.forEach((node) => {
          if (node.textContent?.trim() !== corrected) node.textContent = corrected;
        });
      }
    }

    root.querySelectorAll('.collection-hero__count').forEach((node) => {
      replaceCountText(node, count);
      node.dataset.filteredCollectionCount = String(count);
    });

    root.querySelectorAll('.range-bar__info span').forEach((node) => {
      const currentText = node.textContent || '';
      const isAlmontSummary = /\bAlmont4WD\b/i.test(currentText);
      if (!/\d+\s+(?:Almont4WD\s+)?products?\s+in\s+this\s+(?:filtered\s+)?range/i.test(currentText)) return;
      if (isAlmontSummary) {
        const noun = count === 1 ? 'product' : 'products';
        node.textContent = `${count} Almont4WD ${noun} in this range`;
      } else {
        replaceCountText(node, count);
      }
      node.dataset.filteredCollectionCount = String(count);
    });

    root.querySelectorAll('.collection-hero__stats .stat, .make-hero__stats .stat, .stat-strip .stat').forEach((stat) => {
      const strong = stat.querySelector('strong');
      const label = stat.querySelector('span');
      const value = strong?.textContent?.trim() || '';
      const description = label?.textContent?.trim() || '';

      if (/^\d+\s+products?$/i.test(value) && /in this range|in this filtered range|available for|for this vehicle/i.test(description)) {
        const nextValue = `${count} ${count === 1 ? 'product' : 'products'}`;
        if (strong.textContent !== nextValue) strong.textContent = nextValue;
        if (!/almont4wd for this vehicle/i.test(description) && label.textContent !== 'In this filtered range') {
          label.textContent = 'In this filtered range';
        }
        if (stat.dataset.filteredCollectionCount !== String(count)) {
          stat.dataset.filteredCollectionCount = String(count);
        }
      }

      if (Number.isFinite(filteredCategoryCount) && /^\d+\s+categories?$/i.test(value)) {
        const nextValue = `${filteredCategoryCount} ${filteredCategoryCount === 1 ? 'category' : 'categories'}`;
        if (strong.textContent !== nextValue) strong.textContent = nextValue;
        if (stat.dataset.filteredCollectionCategoryCount !== String(filteredCategoryCount)) {
          stat.dataset.filteredCollectionCategoryCount = String(filteredCategoryCount);
        }
      }

      const exactBrandCount = Number.isFinite(filteredBrandCount) ? filteredBrandCount : context.vendorCount;
      if (exactBrandCount > 0 && /^\d+\s+brands?$/i.test(value)) {
        const nextValue = `${exactBrandCount} ${exactBrandCount === 1 ? 'brand' : 'brands'}`;
        const nextLabel = exactBrandCount === 1 ? 'Selected brand' : 'Selected brands';
        if (strong.textContent !== nextValue) strong.textContent = nextValue;
        if (label.textContent !== nextLabel) label.textContent = nextLabel;
        if (stat.dataset.filteredCollectionBrandCount !== String(exactBrandCount)) {
          stat.dataset.filteredCollectionBrandCount = String(exactBrandCount);
        }
      }
    });

    root.querySelectorAll('a[href="#picks"]').forEach((link) => {
      const text = link.textContent || '';
      if (!/\b\d+\s+(?:[^\n]*?\s+)?products?\b/i.test(text)) return;
      link.textContent = text.replace(/\b\d+\s+(?:[^\n]*?\s+)?products?\b/i, `${count} ${count === 1 ? 'product' : 'products'}`);
    });
  };

  const initialise = () => {
    rememberStats();
    const apply = () => syncFilteredCollectionCounts(document);
    apply();

    const controls = document.querySelector('[data-all-products-controls]');
    const stats = document.querySelector('.collection-hero__stats, .make-hero__stats, .stat-strip');
    if (controls) {
      new MutationObserver(apply).observe(controls, {
        attributes: true,
        attributeFilter: ['data-total-products'],
      });
    }

    document.querySelectorAll('[data-all-products-count-status], #ProductCountDesktop, #ProductCount').forEach((node) => {
      new MutationObserver(apply).observe(node, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    });

    if (stats) {
      new MutationObserver(() => {
        if (Number.isFinite(authoritativeFilteredCount)) {
          syncFilteredCollectionCounts(document, authoritativeFilteredCount);
        }
      }).observe(stats, { childList: true, characterData: true, subtree: true });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }

  document.addEventListener('hoonies:collection-filter-applied', (event) => {
    const count = Number.parseInt(String(event.detail?.matchCount ?? ''), 10);
    if (!Number.isFinite(count) || count < 0) return;
    authoritativeFilteredCount = count;
    syncFilteredCollectionCounts(document, count);
  });

  document.addEventListener('shopify:section:load', (event) => {
    syncFilteredCollectionCounts(event.target);
  });
})();
